// src/components/common/EventToast.tsx
// Positioned toast for event-based feedback with "level-up" animation and ripple ring
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { styled, keyframes, css } from '@mui/material/styles';
import { Box } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { colors } from '../../theme/theme';

interface EventToastProps {
  show: boolean;
  position: { x: number; y: number } | null;
  icon?: React.ReactNode;
  onClose: () => void;
  variant?: 'disc' | 'ring'; // ✅ NEW: Add variant prop
}

// Animation keyframes for DISC variant
const scaleInBounce = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0;
  }
  70% {
    transform: translate(-50%, -50%) scale(1.6);
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
`;

const levelUp = keyframes`
  0% {
    transform: translate(-50%, -50%) translateY(0);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) translateY(-60px);
    opacity: 0;
  }
`;

// Animation keyframes for RING variant (ripple effect)
const radiateOut = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  90% {
    opacity: 0;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.9);
    opacity: 0;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

// Main disc container with proper animation wrapping
const ToastDisc = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isGhost',
})<{ $isGhost?: boolean }>`
  position: fixed;
  width: 52px;
  height: 52px;
  background-color: ${colors.primary.main};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  pointer-events: none;

  /* Icon styling */
  .MuiSvgIcon-root {
    color: #fff;
    font-size: 1.9rem;
  }

  /* Main disc animation - wrapped in css helper */
  ${(props) =>
    !props.$isGhost &&
    css`
      animation: ${scaleInBounce} 0.2s ease-out forwards;
    `}

  /* Ghost disc animation - wrapped in css helper */
  ${(props) =>
    props.$isGhost &&
    css`
      animation: ${levelUp} 0.4s ease-out 0.1s forwards;
    `}
`;

// ✅ NEW: Ring container for ripple effect
const ToastRing = styled(Box)`
  position: fixed;
  width: 44px;
  height: 44px;
  border: 6px solid ${colors.grey[600]};
  border-radius: 50%;
  background-color: transparent;
  z-index: 9999;
  pointer-events: none;

  /* Ripple animation */
  animation: ${radiateOut} 0.5s ease-out forwards;
`;

// Wrapper for both discs/rings at the event position
const ToastWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isFadingOut',
})<{ $isFadingOut: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  pointer-events: none;
  ${(props) =>
    props.$isFadingOut &&
    css`
      animation: ${fadeOut} 0.3s ease-out forwards;
    `}
`;

const EventToast: React.FC<EventToastProps> = ({
  show,
  position,
  icon,
  onClose,
  variant = 'disc', // ✅ NEW: Default to 'disc' for backward compatibility
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // ✅ NEW: Different timing for ring vs disc
  const TIMING_CONFIG =
    variant === 'ring'
      ? {
          // Ring variant: just the ripple animation
          ANIMATION_DURATION: 500, // 0.5s for ripple
          HOLD_DURATION: 0, // No hold
          FADE_OUT_DURATION: 0, // No fade out (ripple handles it)
        }
      : {
          // Disc variant: original timing
          ANIMATION_DURATION: 200, // 0.2s
          HOLD_DURATION: 100, // 0.1s
          FADE_OUT_DURATION: 300, // 0.3s
        };

  useEffect(() => {
    if (show && position) {
      setIsVisible(true);
      setIsFadingOut(false);

      // Total animation time
      const totalTime =
        TIMING_CONFIG.ANIMATION_DURATION +
        TIMING_CONFIG.HOLD_DURATION +
        TIMING_CONFIG.FADE_OUT_DURATION;

      const timer = setTimeout(() => {
        if (variant === 'disc') {
          setIsFadingOut(true);
        }

        // Wait for fade out to complete (or just close immediately for ring)
        setTimeout(() => {
          setIsVisible(false);
          setIsFadingOut(false);
          onClose();
        }, TIMING_CONFIG.FADE_OUT_DURATION || 0);
      }, totalTime);

      return () => clearTimeout(timer);
    }
  }, [show, position, onClose, variant, TIMING_CONFIG]);

  if (!isVisible || !position) return null;

  const defaultIcon = <ContentCopyIcon />;
  const displayIcon = icon || defaultIcon;

  // ✅ NEW: Render different content based on variant
  const toastContent = (
    <ToastWrapper $isFadingOut={isFadingOut}>
      {variant === 'ring' ? (
        // Ring variant: single ripple ring, no icon
        <ToastRing
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ) : (
        // Disc variant: original behavior with main + ghost disc
        <>
          {/* Main disc */}
          <ToastDisc
            style={{
              left: `${position.x}px`,
              top: `${position.y}px`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            {displayIcon}
          </ToastDisc>

          {/* Ghost disc - appears after delay */}
          <ToastDisc
            $isGhost
            style={{
              left: `${position.x}px`,
              top: `${position.y}px`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            {displayIcon}
          </ToastDisc>
        </>
      )}
    </ToastWrapper>
  );

  // Render to portal at document root
  return ReactDOM.createPortal(toastContent, document.body);
};

export default EventToast;
export type { EventToastProps };
